import { useCallback, useEffect, useState } from 'react';
import { FaLightbulb, FaLink, FaPlug } from 'react-icons/fa';
import { Alert } from 'reactstrap';
import {
  EditSection,
  LoadingContainer,
  TabsComponent,
} from '../../components/Layout';
import { ContentSection } from '../../components/Layout/ContentSection';
import { Communicator } from '../../tools/Communication';
import { LayoutStoreInstance } from '../../LayoutStore';
import { getActiveTabFromNav } from '@/utils/url';
import i18n from '@/utils/i18n';
import { Routes } from '@/utils/routes';
import { Button } from '@/components/ui/button';
import { PlusIcon } from 'lucide-react';
import { DataTable } from '@/views/Events/EventOwnerDashboard/HandoutsReturnsTable/DataTable';
import {
  getIntegrationColumns,
  IntegrationRow,
} from './partnerIntegrationsColumns';
import { AddIntegrationModal } from './AddIntegrationModal';
import { WebhookConfigSection } from './WebhookConfigSection';
import {
  CustomIntegrationToken,
  GetIntegrationsResponse,
  INTEGRATION_SERVICES,
} from './integrationServices';

type PartnerIntegrationsTabProps = {
  vytalStoreId: string | null;
  title: string;
};

export const PartnerIntegrationsTab = ({
  vytalStoreId,
  title,
}: PartnerIntegrationsTabProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [integrations, setIntegrations] =
    useState<GetIntegrationsResponse | null>(null);
  const [tokens, setTokens] = useState<{ [key: string]: string }>({});
  const [customTokens, setCustomTokens] = useState<{
    [service: string]: string;
  }>({});
  const [showAddModal, setShowAddModal] = useState(false);

  const loadData = useCallback(async () => {
    if (!vytalStoreId) {
      return;
    }
    try {
      const [integrationsResponse, tokensResponse] = await Promise.all([
        Communicator.Get('/Integration/GetIntegrations', {
          storeId: vytalStoreId,
        }),
        Communicator.Get('/Merchant/GetIntegrationTokens', {
          storeId: vytalStoreId,
        }),
      ]);
      setIntegrations(await integrationsResponse.json());
      const tokenBody = await tokensResponse.json();
      setTokens(tokenBody.tokens);
      setCustomTokens(
        Object.fromEntries(
          (tokenBody.customTokens ?? []).map((t: CustomIntegrationToken) => [
            t.service,
            t.token,
          ]),
        ),
      );
    } catch (e) {
      try {
        const body = await (e as Response).json();
        setError(body.errorMessage);
      } catch {
        setError(i18n.t('Integrations:error'));
      }
    }
    setIsLoading(false);
  }, [vytalStoreId]);

  useEffect(() => {
    // loadData only sets state after awaited fetches, never synchronously
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadData();
  }, [loadData]);

  if (isLoading) {
    return <LoadingContainer />;
  }
  if (error || integrations == null || vytalStoreId == null) {
    const errorMessage = error?.includes('has no users')
      ? i18n.t('Integrations:noUsers')
      : (error ?? i18n.t('Integrations:error'));
    return (
      <Alert color="danger">
        <div className="d-flex">
          <div className="align-self-center">
            <FaLightbulb />
          </div>
          <div className="ms-2">{errorMessage}</div>
        </div>
      </Alert>
    );
  }

  const addIntegrationButton = (
    <Button variant="secondary" size="sm" onClick={() => setShowAddModal(true)}>
      <PlusIcon />
      {i18n.t('Integrations:addIntegration')}
    </Button>
  );

  const addIntegrationModal = (
    <AddIntegrationModal
      isOpen={showAddModal}
      onClose={() => setShowAddModal(false)}
      storeId={vytalStoreId}
      availableServices={integrations.availableServices}
      addedServices={integrations.addedIntegrations.map((i) => i.service)}
      onAdded={loadData}
    />
  );

  if (integrations.addedIntegrations.length === 0) {
    return (
      <ContentSection title={title} collapsable={false} key="content">
        <div className="py-5 text-center">
          <FaPlug size={48} className="text-muted mb-3" />
          <h5>{i18n.t('Integrations:emptyStateTitle')}</h5>
          <p className="text-muted">
            {i18n.t('Integrations:emptyStateDescription')}
          </p>
          {addIntegrationButton}
        </div>
        {addIntegrationModal}
      </ContentSection>
    );
  }

  const customEntries = integrations.addedIntegrations.filter(
    (entry) => entry.isCustom,
  );
  const predefinedEntries = integrations.addedIntegrations.filter(
    (entry) => !entry.isCustom,
  );

  // Logical deletion on the backend (DeletedAt); re-adding the same service
  // brings it back, so this is safe to confirm with a plain dialog.
  const removeIntegration = async (service: string, name: string) => {
    if (
      !window.confirm(i18n.t('Integrations:confirmRemoveIntegration', { name }))
    ) {
      return;
    }
    try {
      await Communicator.PostData('/Integration/RemoveIntegration', {
        storeId: vytalStoreId,
        service,
      });
      await loadData();
    } catch {
      LayoutStoreInstance.setError(i18n.t('Integrations:removeError'));
    }
  };

  // One row per added integration, resolving the display title and token the
  // same way the old per-entry sections did (predefined tokens are keyed by
  // the camelCased token key, custom ones by their slug).
  const integrationRows: IntegrationRow[] = integrations.addedIntegrations.map(
    (entry) => {
      if (entry.isCustom) {
        return {
          service: entry.service,
          name: entry.displayName ?? entry.service,
          isCustom: true,
          createdAt: entry.createdAt,
          token: customTokens[entry.service],
        };
      }
      const serviceInfo = INTEGRATION_SERVICES.find(
        (s) => s.service === entry.service,
      );
      return {
        service: entry.service,
        name: i18n.t(`Integrations:${serviceInfo?.i18nKey ?? entry.service}`),
        isCustom: false,
        createdAt: entry.createdAt,
        token: tokens[serviceInfo?.tokenKey ?? entry.service],
      };
    },
  );

  // Same navbar pattern as MerchantStorePageContent (the /partner/store view):
  // TabsComponent outside the card, each tab bringing its own ContentSection.
  // Webhooks are only editable for custom integrations; predefined services
  // share a Vytal-managed config, explained by the info alert.
  const navTabs = [
    {
      title: i18n.t('Integrations:integrationTypes'),
      icon: <FaPlug />,
      content: (
        <div data-use-bs={true}>
          <ContentSection
            title={i18n.t('Integrations:integrationTypes')}
            collapsable={false}
          >
            {/* data-no-bs: the DataTable is fully shadcn — opt out of the scoped
                Bootstrap styles, like MerchantTransactionsTab does. */}
            <div data-no-bs>
              <DataTable
                columns={getIntegrationColumns({
                  onRemove: removeIntegration,
                  // Dev-only tooling: the backend endpoints reject outside the
                  // dev environment, so the action is hidden with the flag.
                  getSimulateWebhooksHref: integrations.devFeaturesEnabled
                    ? (row) =>
                        Routes.partnerIntegrationWebhooks(
                          row.service,
                          vytalStoreId,
                          window.location.pathname + window.location.search,
                          row.name,
                        )
                    : undefined,
                })}
                data={integrationRows}
                opts={{
                  pageSize: 20,
                  paginationVariant: 'numbered',
                  viewOptionsVariant: 'icon',
                  // Same look as the transactions toolbar's "+ Add a filter"
                  ToolbarRightComponent: (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowAddModal(true)}
                    >
                      <PlusIcon />
                      {i18n.t('Integrations:addIntegration')}
                    </Button>
                  ),
                }}
              />
            </div>
          </ContentSection>
        </div>
      ),
    },
    {
      title: i18n.t('Integrations:setupWebhooks'),
      icon: <FaLink />,
      content: (
        <div data-use-bs={true}>
          <ContentSection
            title={i18n.t('Integrations:setupWebhooks')}
            collapsable={false}
          >
            <EditSection>
              {customEntries.map((entry) => (
                <WebhookConfigSection
                  key={entry.service}
                  storeId={vytalStoreId}
                  service={entry.service}
                  title={entry.displayName ?? entry.service}
                  reminderWebhookUrl={entry.reminderWebhookUrl}
                  returnWebhookUrl={entry.returnWebhookUrl}
                  invoiceWebhookUrl={entry.invoiceWebhookUrl}
                  apiKeyIsSet={entry.apiKeyIsSet}
                  onSaved={loadData}
                />
              ))}
              {predefinedEntries.length > 0 && (
                <Alert color="info">
                  {i18n.t('Integrations:webhookManagedByVytal')}
                </Alert>
              )}
            </EditSection>
          </ContentSection>
        </div>
      ),
    },
  ];

  // TabsComponent tracks flat tabs with plain indices ('0', '1'); default to the
  // first tab when the param is absent (getActiveTabFromNav defaults to '0_0',
  // which only fits dropdown children).
  const activeSubTab =
    new URLSearchParams(window.location.search).get('activeSubTab') ?? '0';

  return (
    <>
      <TabsComponent
        tabs={navTabs}
        isSubTab
        activeTab={getActiveTabFromNav()}
        activeSubTab={activeSubTab}
      />
      {addIntegrationModal}
    </>
  );
};
